"use client";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Dispatch,
  FC,
  FormEvent,
  PropsWithChildren,
  SetStateAction,
  useState,
} from "react";
import { Button } from "../ui/button";
import { useRefresh } from "react-admin";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, Pen } from "lucide-react";
import axiosInstance from "@/utils/axiosInstance";

interface Props extends PropsWithChildren {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  title: string;
  description?: string;
  id: number;
  resource: string;
  data: any;
  responseMessage?: string;
}

const CommonEdit: FC<Props> = ({
  isOpen,
  setIsOpen,
  title,
  description,
  id,
  resource,
  data,
  responseMessage,
  children,
}) => {
  const refresh = useRefresh();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEdit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axiosInstance.put(`/${resource}/${id}`, data);
      toast(responseMessage || "Record updated");
      refresh();
    } catch (err: any) {
      toast(err.response?.data?.message || "Error occurred");
    } finally {
      setIsSubmitting(false);
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ||
              "Fill all the required fields (marked with *). Click save when you are done"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleEdit} className="grid gap-1 py-4">
          {children}
          <DialogFooter className="w-full mt-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CommonEdit;
